/**
 * TV2M(TV-to-Mobile) 계약. `docs/08-tv2m-contracts.md` 가 명세이고 이 파일이 그 구현이다.
 *
 * `tv2m-server` 의 DTO, `tv2m-sdk`, `bloom` 이 전부 여기서 import 한다.
 * 어느 쪽에서도 재선언하지 말 것 (CLAUDE.md 절대 규칙 10).
 */
/** TV2M Server 기준 경로. BLOOM Server 의 `API_BASE_PATH` 와 값은 같지만 호스트가 다르다. */
export const TV2M_API_BASE_PATH = '/v1';
// ── 1. 공통 열거형 ─────────────────────────────────────────────
/** 트리거 소스. v7 신설. 값이 없으면 'ad' 로 간주한다(하위 호환). */
export const TRIGGER_TYPES = ['ad', 'channel_content', 'content_offer'];
export function isTriggerType(value) {
    return TRIGGER_TYPES.includes(value);
}
/** 재생 클라이언트 유형. v7 에서 pc_web / mobile_web 이 추가되었다. */
export const CLIENT_TYPES = ['paytv_stb', 'fast_ott_app', 'pc_web', 'mobile_web'];
export function isClientType(value) {
    return CLIENT_TYPES.includes(value);
}
/** Trigger 응답이 지시하는 전환 방식. 리시버는 이 값으로만 분기한다. */
export const HANDOFF_MODES = ['same_device', 'push', 'qr', 'none'];
export function isHandoffMode(value) {
    return HANDOFF_MODES.includes(value);
}
/**
 * 매칭이 성사된 tier. Match Session 과 Push payload 에 기록된다.
 *
 * **「어떻게 찾았는가」 하나를 뜻한다** (`docs/21` W9). 찾은 뒤에 무슨 일이 있었는지는
 * `MatchSession.status`·`qrScannedAt` 과 `Tv2mEvent` 가 맡는다 — 스캔·만료로 이 값을
 * 덮으면 「가구 경로로 찾은 뒤 QR 이 스캔됐다」를 기록에서 되살릴 수 없다.
 *
 * 그래서 아래 뒤의 둘은 **더 이상 쓰이지 않는다.** 값을 지우는 것은 마이그레이션이고
 * 얻는 것이 없어 남겨 둔다.
 */
export const MATCH_SOURCES = [
    'same_device', // Tier 0
    'account_matched', // Tier 1 (이번 MVP 미구현)
    'server_ip_matched', // Tier 2
    'client_ip_matched', // Tier 3 (이번 MVP 미구현)
    'qr_pending', // Tier 4 발급
    'qr_scanned', // 미사용 — 스캔은 status·qrScannedAt 이 말한다 (docs/21 W9)
    'qr_expired', // 미사용 — 만료는 status 가 말한다 (docs/21 W9)
];
/** Push 발송 채널. MVP 는 webpush 만 구현한다. */
export const PUSH_PROVIDERS = ['fcm', 'apns', 'webpush'];
/** Deep Link 의 source 파라미터. 유입 경로별로 값이 다르다. */
export const TV2M_SOURCES = [
    'tv2m', // Push 알림 터치
    'tv2m_same_device', // Tier 0 직접 유입
    'tv2m_deferred', // QR 경유 설치 후 복원
    'tv2m_same_device_deferred', // 모바일 웹 경유 설치 후 복원
];
export function isTv2mSource(value) {
    return TV2M_SOURCES.includes(value);
}
// ── 3. 모바일 SDK ↔ TV2M Server ────────────────────────────────
/** API Key 헤더 이름. MVP 는 `.env` 의 `TV2M_API_KEY` 단일 값과 단순 비교한다. */
export const TV2M_API_KEY_HEADER = 'X-TV2M-Api-Key';
/**
 * 파트너 **서버** 전용 키의 헤더 (v8). `/v1/match*` 와 reachability 를 보호한다.
 *
 * **위 `TV2M_API_KEY_HEADER` 와 헤더 자체를 가른다.** 값만 다르게 두고 헤더를 공유하면
 * 두 자격이 같은 자리에 실려, 앱 번들에 박힌 키(`TV2M_API_KEY` — 비밀이 아니다)로 온
 * 요청과 진짜 파트너 키로 온 요청을 **경로가 아니라 값으로만** 구분하게 된다.
 * 그 구분이 어긋나는 순간 `/v1/match` 가 브라우저에 박힌 키로 열리고,
 * 그 경로는 `X-Forwarded-For` 를 신뢰하므로 **누구나 남의 가구를 사칭한다**
 * (CLAUDE.md 절대 규칙 18).
 *
 * 헤더가 둘이면 잘못 보낸 요청이 401 로 즉시 드러난다.
 *
 * 보내는 쪽은 `@apm/tv2m-server-sdk`, 받는 쪽은 `tv2m-server` 의 `PartnerApiKeyGuard` 다.
 * 서로 다른 패키지라 문자열을 어느 한쪽에 적으면 계약이 갈라진다 (절대 규칙 10).
 */
export const TV2M_PARTNER_API_KEY_HEADER = 'X-TV2M-Partner-Key';
/**
 * 아래 셋은 T0 에서 문자열 유니온으로만 선언돼 있었다. **값 목록을 함께 둔다** —
 * 서버 DTO 가 `@IsIn` 으로 런타임 검증을 하려면 배열이 필요하고, 목록을 서버에
 * 다시 적으면 계약이 두 곳으로 갈라진다 (CLAUDE.md 절대 규칙 10).
 * 타입의 값 집합은 그대로다.
 */
export const DEVICE_PLATFORMS = ['web', 'android', 'ios'];
/**
 * v20. **BLOOM 쪽 `BloomDevice.platform` 은 enum 이 아니라 `String`** 이라, 그 값을 계약에
 * 실을 때 좁혀야 한다. 좁히지 않고 캐스팅하면 DB 에 들어간 오타가 그대로 계약을 타고
 * 콘솔 화면까지 간다.
 */
export function isDevicePlatform(value) {
    return DEVICE_PLATFORMS.includes(value);
}
export const NETWORK_TYPES = ['wifi', 'cellular', 'unknown'];
export const APP_STATES = ['foreground', 'background'];
/**
 * 앱이 어떤 표시 모드로 열렸는가 (v20). **`platform` 과 다른 축이다.**
 *
 * `platform` 은 브라우저 어댑터가 `'web'` 으로 하드코딩하고 `pushProvider` 가 그것의
 * 순수 함수라, **셸도 홈 화면 앱도 Safari 탭도 저장된 값이 완전히 같다.** 그래서 한
 * 아이폰이 만든 여러 행이 콘솔에서 똑같이 보인다 (`docs/42` §3).
 *
 * **`platform` 을 「고쳐서」 풀지 않는다** — `ios` 로 바꾸는 순간 iOS 홈 화면 PWA 가
 * `apns` 로 등록되고 delegated 발송이 조용히 실패한다 (`docs/43` D3).
 *
 * 값의 출처는 **iOS 전용 비표준 속성 `navigator.standalone`** 하나다. 그것을 읽는 것은
 * 어댑터의 일이고(규칙 13) 코어는 받은 값을 싣기만 한다.
 */
export const DEVICE_DISPLAY_MODES = ['browser', 'standalone'];
/**
 * 기기 활성도. `DeviceIpState.activeStatus` 의 값이다 (`docs/08` §7).
 * 계약 응답에는 아직 나가지 않지만 Tier 2 조회 결과의 필드라 여기 둔다.
 */
export const ACTIVE_STATUSES = ['active', 'idle', 'dormant'];
/**
 * `DeviceRegisterResponse.nextHeartbeatInSeconds` 의 기본값 (`docs/08` §3).
 * 서버가 응답에 싣고 SDK 가 그 주기로 보낸다 — 두 곳에 숫자를 두면 어긋난다.
 */
export const TV2M_HEARTBEAT_INTERVAL_SEC = 300;
// ── 4. Push Payload ────────────────────────────────────────────
/** Push 본문의 판별자. 우리 알림이 아닌 것을 SDK 가 걸러내는 유일한 근거다. */
export const TV2M_PUSH_TYPE = 'tv2m';
/**
 * APNs 본문에서 `Tv2mPushPayload` **원문**이 들어가는 키 (v12, 절대 규칙 39).
 *
 * ```
 * { aps: { alert, sound, thread-id }, [TV2M_APNS_PAYLOAD_KEY]: <payload 원문> }
 * ```
 *
 * `aps` 는 애플이 읽고 이 키 안은 **우리 것**이다 — snake_case 그대로이며, 그래서
 * 네이티브 파서가 `public/sw.js` 와 **같은 필드명**을 읽는다. 그것이 규칙 11 이
 * 지키려던 것 전부다.
 *
 * **`TV2M_PUSH_TYPE` 과 값이 같은 것은 우연이다.** 저쪽은 payload 안의 `type` 필드값
 * (「우리 알림인가」)이고 이쪽은 봉투의 키(「payload 가 어디 있는가」)다 — 한쪽을 바꿔야
 * 하는 날 다른 쪽까지 따라가면 안 되므로 상수를 나눠 둔다.
 *
 * 쓰는 쪽이 셋이고 **그중 하나는 Swift 라 타입이 닿지 않는다** (`AppDelegate.swift`).
 * 그래서 `native-contract.test.ts` 가 그 파일을 원문으로 읽어 이 값과 대조한다 —
 * 어긋나도 화면에는 증상이 없기 때문이다(알림은 뜨고, 눌러도 방송이 뜬다).
 */
export const TV2M_APNS_PAYLOAD_KEY = 'tv2m';
/**
 * 알림 제목 (`docs/08` §4 SV-CC-04 / TS-02).
 *
 * **여기 두는 이유는 만드는 쪽과 폴백하는 쪽이 다른 패키지이기 때문이다** —
 * 채우는 것은 `@apm/tv2m-server-sdk` 의 `core/payload.ts` 이고, payload 의 `title` 이 비어 있거나
 * Push 가 아닌 경로(Deferred 복원)에서 같은 문구를 세우는 것은 `tv2m-sdk` 다.
 * 두 곳에 적어 두면 한쪽만 고쳐졌을 때 **같은 유입인데 알림 제목만 다르게** 나가고,
 * 그것은 코드를 봐야만 드러난다 (절대 규칙 10 과 같은 이유다).
 *
 * `Record<TriggerType, string>` 이라 소스가 늘면 여기가 컴파일 에러로 드러난다.
 * 빠뜨린 소스에 빈 제목이 나가는 것보다 빌드가 깨지는 편이 낫다 —
 * **발송된 알림은 되돌릴 수 없다.**
 */
export const TV2M_NOTIFICATION_TITLES = {
    ad: '📺 TV에서 본 상품',
    channel_content: '📺 방송 중인 상품',
    // v23. **광고가 아니라 장면이다** — 시청자는 광고를 본 것이 아니라 드라마를 보다가
    // 그 장면의 상품을 골랐다. 문구가 그 사실을 말하지 않으면 폰에 뜬 알림이 광고와
    // 구분되지 않는다.
    content_offer: '🛍 방금 그 장면의 상품',
};
/**
 * 본문 폴백. 서버는 여기에 `{상품명} · {가격}원` 을 앞세우지만(`docs/08` §4),
 * SDK 는 상품명·가격을 모른다 — payload 에 본문이 없는 상황이 곧 그 정보가 없는 상황이다.
 * 그래서 SDK 쪽 폴백은 꼬리 문구만 쓴다.
 */
export const TV2M_NOTIFICATION_BODY_SUFFIX = '지금 모바일에서 구매하세요';
// ── 5. Deep Link ───────────────────────────────────────────────
/**
 * Deep Link 경로. `{MOBILE_APP_BASE_URL}` 뒤에 붙는다.
 * 네이티브 포팅 시 베이스가 커스텀 스킴으로 바뀌어도 이 경로와 쿼리 이름은 유지된다.
 */
export const TV2M_DEEP_LINK_PATH = '/tv2m/product';
export const TV2M_LINK_PARAMS = {
    productId: 'id',
    campaignId: 'campaign_id',
    source: 'source',
    triggerType: 'trigger_type',
    matchId: 'match_id',
    channelId: 'channel_id',
    contentId: 'content_id',
    coupon: 'coupon',
};
/**
 * Deep Link 를 만든다. 베이스에 이미 붙어 있는 쿼리스트링과 프래그먼트를 보존한다.
 *
 * `URL` 을 쓰지 않는 이유는 `buildTrackingUrl` 과 같다 — 이 패키지는 DOM 도 Node 도 없는
 * `lib: ES2022` 로 컴파일된다. 계약 타입이 플랫폼에 묶이면 코어 이식성 근거가 무너진다.
 */
export function buildTv2mDeepLink(baseUrl, ctx) {
    const hashAt = baseUrl.indexOf('#');
    const withoutFragment = hashAt === -1 ? baseUrl : baseUrl.slice(0, hashAt);
    const fragment = hashAt === -1 ? '' : baseUrl.slice(hashAt);
    const queryAt = withoutFragment.indexOf('?');
    const rawPath = queryAt === -1 ? withoutFragment : withoutFragment.slice(0, queryAt);
    const existingQuery = queryAt === -1 ? '' : withoutFragment.slice(queryAt + 1);
    // 베이스 끝의 '/' 는 버린다. 두 개가 겹치면 경로가 달라져 파서가 아니라 라우터가 먼저 깨진다.
    const path = rawPath.replace(/\/+$/, '') + TV2M_DEEP_LINK_PATH;
    const pairs = [
        [TV2M_LINK_PARAMS.productId, ctx.productId],
        [TV2M_LINK_PARAMS.campaignId, ctx.campaignId],
        [TV2M_LINK_PARAMS.source, ctx.source],
        [TV2M_LINK_PARAMS.triggerType, ctx.triggerType],
        [TV2M_LINK_PARAMS.matchId, ctx.matchId],
        [TV2M_LINK_PARAMS.channelId, ctx.channelId],
        [TV2M_LINK_PARAMS.contentId, ctx.contentId],
        [TV2M_LINK_PARAMS.coupon, ctx.coupon],
    ];
    const query = pairs
        .filter((pair) => pair[1] !== undefined)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
    const merged = existingQuery === '' ? query : `${existingQuery}&${query}`;
    return `${path}?${merged}${fragment}`;
}
/**
 * Deep Link 에서 컨텍스트를 되읽는다. 필수 네 값이 없거나 열거값 밖이면 `null`.
 *
 * 경로(`/tv2m/product`)는 검사하지 않는다 — 네이티브 포팅 시 베이스가 커스텀 스킴으로
 * 바뀌므로 경로에 기대면 파서가 웹 전용이 된다. 판별은 필수 파라미터가 맡는다.
 */
export function parseTv2mDeepLink(url) {
    const params = parseQuery(url);
    const productId = params[TV2M_LINK_PARAMS.productId];
    const campaignId = params[TV2M_LINK_PARAMS.campaignId];
    const source = params[TV2M_LINK_PARAMS.source];
    const triggerType = params[TV2M_LINK_PARAMS.triggerType];
    if (!productId || !campaignId)
        return null;
    if (!isTv2mSource(source) || !isTriggerType(triggerType))
        return null;
    const ctx = { productId, campaignId, source, triggerType };
    const matchId = params[TV2M_LINK_PARAMS.matchId];
    const channelId = params[TV2M_LINK_PARAMS.channelId];
    const contentId = params[TV2M_LINK_PARAMS.contentId];
    const coupon = params[TV2M_LINK_PARAMS.coupon];
    if (matchId)
        ctx.matchId = matchId;
    if (channelId)
        ctx.channelId = channelId;
    if (contentId)
        ctx.contentId = contentId;
    if (coupon)
        ctx.coupon = coupon;
    return ctx;
}
/** 프래그먼트를 떼고 쿼리스트링만 해석한다. `URLSearchParams` 도 쓸 수 없다. */
function parseQuery(url) {
    const hashAt = url.indexOf('#');
    const withoutFragment = hashAt === -1 ? url : url.slice(0, hashAt);
    const queryAt = withoutFragment.indexOf('?');
    if (queryAt === -1)
        return {};
    const result = {};
    for (const chunk of withoutFragment.slice(queryAt + 1).split('&')) {
        if (chunk === '')
            continue;
        const eq = chunk.indexOf('=');
        const key = decodeURIComponent(eq === -1 ? chunk : chunk.slice(0, eq));
        const value = eq === -1 ? '' : decodeURIComponent(chunk.slice(eq + 1));
        // 같은 키가 두 번 오면 앞의 것을 남긴다. 우리가 만든 링크에 덧붙은 값을 신뢰하지 않는다.
        if (key in result)
            continue;
        result[key] = value;
    }
    return result;
}
/**
 * QR 랜딩(`GET /q/{token}`)이 설치 안내 화면으로 넘길 때 붙이는 쿼리 (`docs/09` T5).
 *
 * `docs/08` §5 는 Deep Link 만 적고 이 값을 적지 않았다. **여기 두는 이유는 만드는 쪽과
 * 읽는 쪽이 다른 패키지이기 때문이다** — 만드는 것은 `tv2m-server` 의 QR 랜딩이고 읽는
 * 것은 `tv2m-sdk` 의 첫 실행 판정이다. 어느 한쪽에 문자열을 적으면 계약이 두 곳으로
 * 갈라지고(절대 규칙 10), 어긋났을 때의 증상은 **설치하고 돌아왔는데 상품이 없다**라서
 * 어느 쪽이 틀렸는지 화면에 드러나지 않는다.
 *
 * 접두사를 붙이는 이유는 `ref` 가 마케팅 유입 파라미터로 이미 흔하기 때문이다. 우리가 낸
 * 값인지를 값 자체로 판별할 수 있어야 한다.
 */
export const TV2M_INSTALL_REF_PARAM = 'ref';
export const TV2M_QR_REF_PREFIX = 'tv2m_qr_';
export function buildQrInstallRef(sessionToken) {
    return `${TV2M_QR_REF_PREFIX}${sessionToken}`;
}
/** 우리가 낸 `ref` 면 세션 토큰을, 아니면 `null`. 빈 토큰(`tv2m_qr_`)도 `null` 이다. */
export function parseQrInstallRef(ref) {
    if (!ref || !ref.startsWith(TV2M_QR_REF_PREFIX))
        return null;
    const token = ref.slice(TV2M_QR_REF_PREFIX.length);
    return token === '' ? null : token;
}
// ── 6. 전환 이벤트 ─────────────────────────────────────────────
export const TV2M_EVENT_TYPES = [
    'trigger_fired', // 리시버: /v1/trigger 호출 성공
    'push_received',
    'push_displayed',
    'push_clicked',
    'same_device_handoff_returned', // 서버가 Tier 0 응답을 반환
    // 모바일 앱이 Deep Link 로 기동됨. **v8 에서는 delegated 경로의 것으로 남는다** —
    // federated Tier 0 은 페이지를 옮기지 않으므로 `product_sheet_opened` 로 센다.
    // 두 곳에서 세면 전환율이 부풀려진다 (`docs/13` §8).
    'same_device_handoff_clicked',
    'same_device_app_missing', // 웹 리시버가 앱 미설치를 감지
    'qr_displayed',
    'qr_scanned',
    'deferred_link_resolved',
    'product_page_opened',
    /**
     * **의미가 v8 에서 바뀌었다.** "외부 판매처에서 결제가 끝났다"가 아니라
     * "데모용 완료 화면에 도달했다"이다. 실제 결제는 BLOOM 밖에서 일어나고
     * 우리는 그것을 관측할 수 없다 — 이탈 시점까지가 우리가 아는 전부이며
     * 그 자리는 `external_checkout_opened` 다.
     */
    'purchase_completed',
    // ── v8 신설 넷 (`docs/13` §2.3) ──
    'account_linked',
    'account_unlinked',
    /** Tier 0: 같은 앱 안에서 상품 오버레이가 열렸다. **딥링크 기동이 아니다** */
    'product_sheet_opened',
    /** `[판매처로 이동]` — BLOOM 을 떠난 순간 (절대 규칙 14) */
    'external_checkout_opened',
];
export function isTv2mEventType(value) {
    return TV2M_EVENT_TYPES.includes(value);
}
/**
 * `POST /v1/events/bulk` 상한. BLOOM Server 의 `EVENT_BATCH_MAX` 와 값은 같지만
 * 엔드포인트가 다르므로 별도로 둔다 — 한쪽이 바뀌어도 다른 쪽이 따라가지 않는다.
 */
export const TV2M_EVENT_BATCH_MAX = 50;
//# sourceMappingURL=tv2m.js.map